import * as React from "react";
import { Alert, notification } from "antd";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { PageLayout } from "src/components/PageLayout";
import { observer } from "mobx-react-lite";
import { LoaderBox } from "src/components/LoaderBox";
import { PageUrl } from "src/routes/PageUrl";
import { breadItem } from "src/common/breadItem";
import { makeUrl } from "src/routes/makeUrl";
import { emmitLastObjectUpdateSE } from "src/common/storageEventUtils";
import { parseInitialEntityParams } from "src/common/entity/makeInintialEntityParams";
import { t } from "i18next";
import { msgMultiInstancesCreated } from "src/components/EntityEditor";
import { entityCardStore as pageStore } from "./EntityCardStore";
import { EdCardValues } from "./apiEntityCard";
import { EntityFormWithAnchor2 } from "./EntityFormWithAnchor";
import styles from "./EntityCardPage.module.less";
import { useEntityNavBlocker } from "./useEntityNavBlocker";
import { EntityConfirmModal } from "./EntityConfirmModal";
import { EntityCardTitle } from "./EntityCardTitle";

export const NewEntityCardPage: React.FC = observer(() => {
  const { objectId } = useParams();
  const [searchParams] = useSearchParams();
  React.useEffect(() => {
    if (objectId)
      pageStore.initNew(objectId, parseInitialEntityParams(searchParams));
  }, [objectId]);
  if (!objectId) {
    return <Alert type="error" message={t("No object specified")} showIcon />;
  }
  return (
    <PageLayout
      documentTitle={pageStore.documentTitle}
      breadcrumb={[
        breadItem(pageStore.objectName, PageUrl.entities, {
          objectId: pageStore.objectId,
        }),
        breadItem(pageStore.entityName),
      ]}
    >
      <div className={styles.newEntityContent}>
        <LoaderBox remoteData={pageStore.info} drawReady={() => <Inner />} />
      </div>
      <EntityConfirmModal store={pageStore} />
    </PageLayout>
  );
});

const Inner: React.FC = observer(() => {
  useEntityNavBlocker(pageStore);
  const { rootBlock } = pageStore;
  const navigate = useNavigate();
  const { objectId } = useParams();
  const onSuccess = (values: EdCardValues) => {
    if (Array.isArray(values)) {
      notification.success({
        message: msgMultiInstancesCreated(values.length),
      });
      navigate(makeUrl(PageUrl.entities, { objectId: pageStore.objectId }));
      return;
    }
    const { entityId } = values;
    if (!entityId) {
      notification.warning({ message: t("EntityId value not received") });
    } else {
      notification.success({ message: t("Executed instance creation") });
      emmitLastObjectUpdateSE(objectId);
      navigate(makeUrl(PageUrl.entityCard, { id: entityId }), {
        replace: true,
      });
    }
  };
  if (!rootBlock) return null;
  return (
    <EntityFormWithAnchor2
      title={<EntityCardTitle />}
      store={pageStore}
      submitText={pageStore.textOfCreateButton}
      onSubmit={(values) => pageStore.create(values)}
      onSuccess={onSuccess}
      disabled={false}
      availableStates={undefined}
    />
  );
});
